import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useId, useMemo, useState, type FormEvent } from "react";
import { toast } from "sonner";

import {
  getCompetitorControllerGetCompetitorListQueryKey,
  useCompetitorControllerCreateManualCompetitor,
  useCompetitorControllerSearchSku,
} from "@/api/types/main";
import type {
  CompetitorRelationListResponse,
  SearchCompetitorResponse,
} from "@/api/types/main/schemas";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useDebounced } from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";

interface AddCompetitorProps {
  productSku: number;
  date: string;
}

function getApiErrorMessage(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;

  const data = error.response?.data as { message?: unknown } | undefined;

  return typeof data?.message === "string" ? data.message : undefined;
}

export function AddCompetitor({ productSku, date }: AddCompetitorProps) {
  const queryClient = useQueryClient();
  const inputId = useId();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<SearchCompetitorResponse | null>(null);

  const debouncedSearch = useDebounced(search.trim(), 350);

  const parsedSku = Number(debouncedSearch);
  const isSearchLongEnough = debouncedSearch.length >= 4;
  const isSearchNumeric = Number.isSafeInteger(parsedSku) && parsedSku > 0;
  const canSearch = open && isSearchLongEnough && isSearchNumeric;

  const searchSku = canSearch ? parsedSku : 0;

  const listQueryKey = useMemo(
    () =>
      getCompetitorControllerGetCompetitorListQueryKey(productSku, {
        date,
      }),
    [productSku, date]
  );

  const {
    data: results = [],
    isFetching,
    isError: isSearchError,
    error: searchError,
  } = useCompetitorControllerSearchSku(
    {
      sku: searchSku,
    },
    {
      query: {
        enabled: canSearch,
        staleTime: 30_000,
      },
    }
  );

  const resetForm = () => {
    setSearch("");
    setSelected(null);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (!nextOpen) {
      resetForm();
    }
  };

  const handleSearchChange = (value: string) => {
    const onlyDigits = value.replace(/\D/g, "");

    setSearch(onlyDigits);
    setSelected(null);
  };

  const { mutate, isPending } = useCompetitorControllerCreateManualCompetitor({
    mutation: {
      onMutate: async ({ data }) => {
        await queryClient.cancelQueries({
          queryKey: listQueryKey,
          exact: true,
        });

        const previous = queryClient.getQueryData<CompetitorRelationListResponse[]>(listQueryKey);

        const alreadyExists = previous?.some((row) => row.sku === data.competitorSku);

        if (alreadyExists) {
          return { previous };
        }

        const optimisticRow: CompetitorRelationListResponse = {
          sku: data.competitorSku,
          brand: selected?.brand ?? "",
          productName: selected?.name ?? "",
          MPLink: "",
          isMarked: false,
          revenue: 0,
          categoryPercent: 0,
          orderCount: 0,
          avgPrice: 0,
          rating: 0,
          reviewCount: 0,
          daysInStock: 0,
          categoryPosition: 0,
          avgAdPosition: 0,
          avgOrganicPosition: 0,
        };

        queryClient.setQueryData<CompetitorRelationListResponse[]>(listQueryKey, (old) =>
          old ? [optimisticRow, ...old] : [optimisticRow]
        );

        return { previous };
      },

      onError: (error, _variables, context) => {
        if (context?.previous) {
          queryClient.setQueryData(listQueryKey, context.previous);
        }

        const apiMessage = getApiErrorMessage(error);

        toast.error(
          apiMessage
            ? `Не удалось добавить конкурента. ${apiMessage}`
            : "Не удалось добавить конкурента"
        );
      },

      onSuccess: () => {
        toast.success("Конкурент добавлен");
        setOpen(false);
        resetForm();
      },

      onSettled: () =>
        queryClient.invalidateQueries({
          queryKey: listQueryKey,
          exact: true,
        }),
    },
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selected) {
      toast.warning("Выберите конкурента из списка");
      return;
    }

    if (selected.competitorSku === productSku) {
      toast.warning("Нельзя добавить товар конкурентом самому себе");
      return;
    }

    const currentList = queryClient.getQueryData<CompetitorRelationListResponse[]>(listQueryKey);

    const alreadyExists = currentList?.some((row) => row.sku === selected.competitorSku);

    if (alreadyExists) {
      toast.info("Этот конкурент уже добавлен");
      return;
    }

    mutate({
      sku: productSku,
      data: {
        competitorSku: selected.competitorSku,
      },
    });
  };

  const searchErrorMessage = getApiErrorMessage(searchError);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="hover:text-primary text-xs 2xl:text-sm h-8 2xl:h-12"
        >
          Добавить конкурента
        </Button>
      </SheetTrigger>

      <SheetContent className="flex h-full flex-col p-0 sm:max-w-xl">
        <SheetHeader className="border-b px-4 py-4 text-left">
          <SheetTitle>Добавить конкурента</SheetTitle>
          <SheetDescription>
            Введите SKU конкурента, выберите товар из найденных результатов и добавьте его в список.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <ScrollArea className="min-h-0 flex-1">
            <div className="flex flex-col gap-4 px-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor={inputId}>SKU конкурента</Label>

                <Input
                  id={inputId}
                  value={search}
                  onChange={(event) => handleSearchChange(event.target.value)}
                  placeholder="Например, 123456789"
                  inputMode="numeric"
                  autoComplete="off"
                  disabled={isPending}
                />

                <p className="text-xs text-muted-foreground">
                  Поиск начнётся после ввода минимум 4 цифр.
                </p>
              </div>

              <div className="grid gap-2">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium">Результаты поиска</p>

                  {isFetching && <p className="text-xs text-muted-foreground">Ищем...</p>}
                </div>

                {!search && (
                  <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                    Введите SKU конкурента, чтобы начать поиск.
                  </div>
                )}

                {search.length > 0 && search.length < 4 && (
                  <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                    Введите ещё {4 - search.length} символа.
                  </div>
                )}

                {search && isSearchLongEnough && !isSearchNumeric && (
                  <div className="rounded-lg border border-destructive/40 p-4 text-sm text-destructive">
                    SKU должен быть положительным числом.
                  </div>
                )}

                {isSearchError && (
                  <div className="rounded-lg border border-destructive/40 p-4 text-sm text-destructive">
                    {searchErrorMessage ?? "Не удалось выполнить поиск"}
                  </div>
                )}

                {canSearch && !isFetching && !isSearchError && results.length === 0 && (
                  <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                    По этому SKU ничего не найдено.
                  </div>
                )}

                {results.length > 0 && (
                  <div role="listbox" aria-label="Найденные конкуренты" className="grid gap-2">
                    {results.map((item) => {
                      const isSelected = selected?.competitorSku === item.competitorSku;

                      const isCurrentProduct = item.competitorSku === productSku;

                      return (
                        <button
                          key={item.competitorSku}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          disabled={isPending}
                          onClick={() => setSelected(item)}
                          className={cn(
                            "w-full rounded-lg border p-3 text-left transition-colors",
                            "hover:bg-accent hover:text-accent-foreground",
                            "disabled:pointer-events-none disabled:opacity-50",
                            isSelected && "border-primary bg-accent",
                            isCurrentProduct && "border-destructive/40"
                          )}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 space-y-1">
                              <p className="truncate text-sm font-medium">
                                {item.name ?? "Без названия"}
                              </p>

                              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                <span>SKU: {item.competitorSku}</span>

                                {item.brand && <span>Бренд: {item.brand}</span>}
                              </div>

                              {isCurrentProduct && (
                                <p className="text-xs text-destructive">
                                  Это текущий товар. Его нельзя добавить как конкурента.
                                </p>
                              )}
                            </div>

                            {isSelected && (
                              <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                                Выбран
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>

          <SheetFooter className="border-t px-4 py-4">
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-h-5 text-xs text-muted-foreground">
                {selected ? (
                  <>
                    Выбран SKU:{" "}
                    <span className="font-medium text-foreground">{selected.competitorSku}</span>
                  </>
                ) : (
                  "Выберите товар из списка"
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isPending}
                  onClick={() => {
                    setOpen(false);
                    resetForm();
                  }}
                >
                  Отмена
                </Button>

                <Button
                  type="submit"
                  disabled={!selected || selected.competitorSku === productSku || isPending}
                >
                  {isPending ? "Добавляем..." : "Добавить"}
                </Button>
              </div>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
